import axiosInstance from './axiosConfig';

export interface SMSSetting {
  value: string;
  description: string;
  id: number;
}

export interface SMSSettings {
  [key: string]: SMSSetting;
}

export interface CostStats {
  date: string;
  message_type: string;
  message_count: number;
  total_cost: number;
  avg_cost: number;
  successful: number;
  failed: number;
}

export interface TotalCost {
  total_cost: number;
  total_messages: number;
  successful_cost: number;
  failed_cost: number;
}

export interface TemplateVariables {
  pickupNotification: string[];
  statusUpdate: string[];
  paymentReminder: string[];
  customMessage: string[];
}

class SMSSettingsService {

  async getSettings(): Promise<SMSSettings> {
    try {
      const response = await axiosInstance.get('/sms-settings');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch SMS settings');
    }
  }

  async updateSetting(key: string, value: string): Promise<any> {
    try {
      const response = await axiosInstance.put('/sms-settings/setting', { key, value });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update SMS setting');
    }
  }

  async updateTemplate(templateType: string, template: string): Promise<any> {
    try {
      const response = await axiosInstance.put('/sms-settings/template', { templateType, template });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update SMS template');
    }
  }

  async getCostStats(filters?: {
    startDate?: string;
    endDate?: string;
    messageType?: string;
  }): Promise<CostStats[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.messageType) params.append('messageType', filters.messageType);

      const response = await axiosInstance.get(`/sms-settings/cost-stats?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch SMS cost stats');
    }
  }

  async getTotalCost(filters?: {
    startDate?: string;
    endDate?: string;
    messageType?: string;
  }): Promise<TotalCost> {
    try {
      const params = new URLSearchParams();
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.messageType) params.append('messageType', filters.messageType);

      const response = await axiosInstance.get(`/sms-settings/total-cost?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch total SMS cost');
    }
  }

  async getTemplateVariables(): Promise<TemplateVariables> {
    try {
      const response = await axiosInstance.get('/sms-settings/template-variables');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch template variables');
    }
  }

  async testTemplate(templateType: string, template: string, sampleData?: any): Promise<{
    success: boolean;
    parsedMessage: string;
    messageLength: number;
  }> {
    try {
      const response = await axiosInstance.post('/sms-settings/test-template', { templateType, template, sampleData });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to test template');
    }
  }
}

export const smsSettingsService = new SMSSettingsService(); 